import { useContext, useEffect, useRef, useState } from "react";
import {
  createSandboxedIframe,
  installPostMessageType,
  loadEwa,
  postMessageToIframe
} from "../../../libraries/ewa/helpers";
import Context from "../../../context";
import { VamoosInterface } from "../../../libraries/ewa/vamoosSetup";
import { useNavigate } from "react-router-dom";
import { EwaContext } from "../../../context/EwaContext";
import * as esbuild from "esbuild-wasm";

const EWA = () => {
  const navigate = useNavigate();

  const {
    itin,
    isUserAuthenticated,
    userAuthInfo,
    ewaSettings,
    setEwaSettings,
    setGlobalModalContent,
    t,
    screenNameOverride,
    setScreenNameOverride
  } = useContext(Context);
  const { setEwaOpenDocumentInfo, setEwaOpenDocumentActive, isBuilderInitialised, setIsBuilderInitialised, } = useContext(EwaContext);
  const [installIframes, setInstallIframes] = useState([]);
  const [readyIframeCount, setReadyIframeCount] = useState(0);
  const [isIframesInstalled, setIsIframesInstalled] = useState(false);

  const containerRef = useRef(null);
  const cleanupFunctionsRef = useRef([]);
  const initTrackerRef = useRef(null);
  const readyCountRef = useRef(0);
  const screenNameOverrideRef = useRef(screenNameOverride);
  const currentUserRef = useRef(null);

  useEffect(() => {
    screenNameOverrideRef.current = screenNameOverride;
  }, [screenNameOverride]);

  useEffect(() => {
    if (!itin?.id) return;

    const currentUserData = isUserAuthenticated
      ? userAuthInfo?.logins?.all.find(login => login.id === itin?.id)
      : itin?.localData?.client;
    const identityProvider = localStorage.getItem("loginId");

    currentUserRef.current = {
      name: currentUserData?.name ?? currentUserData?.fullname,
      friendlyName: itin?.localData?.client?.friendlyName ?? currentUserData?.friendlyName,
      ...(isUserAuthenticated ? { primaryEmail: userAuthInfo?.primaryEmail } : {}),
      ...(currentUserData?.emails ? { emails: currentUserData?.emails } : {}),
      ...(identityProvider ? { identityProvider } : {}),
    }
  }, [isUserAuthenticated, itin?.clients, itin?.id, userAuthInfo, itin?.localData]);

  useEffect(() => {
    if (!itin || !ewaSettings?.docs || (isUserAuthenticated && !userAuthInfo)) return;
    if (initTrackerRef.current === itin.id) return;

    const currentItinClient = itin?.clients?.find(client => client?.isCurrent);
    if (itin?.clients?.length && !currentItinClient) {
      // fallback for cases where the user can reload the page before selecting a traveller
      setEwaSettings(prev => ({
        ...prev,
        phase: "install",
      }));
      return;
    }

    const { localData, ...currentItin } = itin;
    VamoosInterface._init(
      currentItin,
      () => currentUserRef.current,
      setGlobalModalContent,
      t,
      navigate,
      localData.urlKey,
      () => screenNameOverrideRef.current,
      setScreenNameOverride,
      setEwaOpenDocumentInfo,
      setEwaOpenDocumentActive
    );
    setEwaSettings(prev => ({
      ...prev,
      isLoading: false,
    }));
    initTrackerRef.current = itin.id;

  }, [setEwaOpenDocumentInfo, navigate, screenNameOverride, isUserAuthenticated, itin, userAuthInfo, setEwaSettings, ewaSettings?.docs, setGlobalModalContent, t, setScreenNameOverride, setEwaOpenDocumentActive]);

  // attach listeners to handle storage API requests from untrusted EWAs
  useEffect(() => {
    if (!ewaSettings?.docs?.some(doc => !doc?.trusted)) return;

    const handleStorageMessage = (event) => {
      const data = event.data;
      if (!data || !data.type) return;

      const resolveTarget = (storage) =>
        storage === "localStorage" ? window.localStorage : window.sessionStorage;

      switch (data.type) {
        case "ewa-storage-set": {
          const { storage, key, value, prefix } = data;
          const target = resolveTarget(storage);
          target.setItem(prefix + key, value);
          event.source?.postMessage(
            { type: "ewa-storage-update", storage, prefix, op: "set", key, value },
            "*"
          );
          break;
        }

        case "ewa-storage-remove": {
          const { storage, key, prefix } = data;
          const target = resolveTarget(storage);
          target.removeItem(prefix + key);
          event.source?.postMessage(
            { type: "ewa-storage-update", storage, prefix, op: "remove", key },
            "*"
          );
          break;
        }

        case "ewa-storage-clear": {
          const { storage, prefix } = data;
          const target = resolveTarget(storage);
          Object.keys(target).forEach((k) => {
            if (k.startsWith(prefix)) target.removeItem(k);
          });
          event.source?.postMessage(
            { type: "ewa-storage-update", storage, prefix, op: "clear" },
            "*"
          );
          break;
        }

        case "ewa-storage-direct-set": {
          const { storage, key, value, prefix } = data;
          const target = resolveTarget(storage);
          target.setItem(prefix + key, value);
          event.source?.postMessage(
            { type: "ewa-storage-update", storage, prefix, op: "set", key, value },
            "*"
          );
          break;
        }

        case "ewa-storage-direct-delete": {
          const { storage, key, prefix } = data;
          const target = resolveTarget(storage);
          target.removeItem(prefix + key);
          event.source?.postMessage(
            { type: "ewa-storage-update", storage, prefix, op: "remove", key },
            "*"
          );
          break;
        }

        case "ewa-storage-init": {
          const { storage, prefix, callId } = data;
          const target = resolveTarget(storage);
          const snapshot = {};
          Object.keys(target).forEach(k => {
            if (k.startsWith(prefix)) {
              snapshot[k.slice(prefix.length)] = target.getItem(k);
            }
          });
          event.source?.postMessage({ type: "storage-response", callId, value: snapshot }, "*");
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener("message", handleStorageMessage);

    const cleanup = () => {
      window.removeEventListener("message", handleStorageMessage);
    };
    cleanupFunctionsRef.current.push(cleanup);

    return cleanup;
  }, [ewaSettings?.docs]);


  useEffect(() => {
    if (ewaSettings?.docs && !ewaSettings?.isLoading) {
      const iframeElements = ewaSettings?.docs.map((doc) => createSandboxedIframe(doc?.trusted, true, doc.id, installPostMessageType, itin?.id));
      if (containerRef.current) {
        iframeElements.forEach((iframe) => {
          containerRef.current.appendChild(iframe);
        });
      }
      setInstallIframes(iframeElements);
    }
  }, [ewaSettings?.docs, ewaSettings?.isLoading, itin?.id]);

  // clean-up logic. Runs on each ItineraryLoader.login invocation
  useEffect(() => {
    if (!ewaSettings?.docs && isIframesInstalled) {
      installIframes.forEach((iframe) => {
        postMessageToIframe(iframe, "vamoosEvent", "clearEventListeners");
      });
      cleanupFunctionsRef.current.forEach((cleanupFn) => cleanupFn && cleanupFn());
      cleanupFunctionsRef.current = [];
      containerRef.current.innerHTML = "";
      setInstallIframes([]);
      setReadyIframeCount(0);
      setIsIframesInstalled(false);
      initTrackerRef.current = null;
      readyCountRef.current = 0;
    }
  }, [ewaSettings?.docs, installIframes, isIframesInstalled]);

  useEffect(() => {
    if (!ewaSettings?.docs?.length) return;
    const handleMessage = (event) => {
      if (event.data?.type === "iframe-ready") {
        readyCountRef.current += 1;
        setReadyIframeCount(readyCountRef.current);

        if (readyCountRef.current === ewaSettings.docs.length) window.removeEventListener("message", handleMessage);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      readyCountRef.current = 0
      window.removeEventListener("message", handleMessage);
      cleanupFunctionsRef.current.forEach((cleanupFn) => cleanupFn && cleanupFn());
      cleanupFunctionsRef.current = [];
    };
  }, [ewaSettings?.docs?.length]);

  useEffect(() => {
    if (isBuilderInitialised || !ewaSettings?.docs?.length) return;

    const initBuilder = async () => {
      await esbuild.initialize({
        wasmURL: "/esbuild.wasm",
      });
      setIsBuilderInitialised(true);
    }

    initBuilder();
  }, [ewaSettings?.docs?.length, isBuilderInitialised, setIsBuilderInitialised])

  useEffect(() => {
    if (readyIframeCount !== ewaSettings?.docs?.length || !itin?.id || isIframesInstalled || ewaSettings?.isLoading || !isBuilderInitialised) return;
    let canceled = false;

    const runAsyncLoadEwa = async () => {
      const validIframes = [];
      for (const [index, iframe] of installIframes.entries()) {
        try {
          const cleanup = await loadEwa(ewaSettings?.docs[index]?.file?.httpsUrl, iframe, itin.id, installPostMessageType);

          if (canceled) return;

          // Remove iframe from container if install.js is missing
          if (cleanup === null) {
            if (containerRef.current?.contains(iframe)) containerRef.current.removeChild(iframe);
            continue;
          }

          cleanupFunctionsRef.current.push(cleanup);
          validIframes.push(iframe);
          postMessageToIframe(iframe, "vamoosEvent", "load");
          postMessageToIframe(iframe, "vamoosEvent", ewaSettings?.phase);

        } catch (err) {
          console.error("Failed to load EWA iframe:", err);
          setGlobalModalContent(() => ({
            closable: true,
            message: err?.modalMessage || err?.message || 'Failed to load EWA iframe',
            buttons: [{ text: t('ok'), type: 'close' }]
          }));

          // Ensure this iframe doesn’t stay in container
          if (iframe.parentNode === containerRef.current) containerRef.current.removeChild(iframe);
        }
      }
      setIsIframesInstalled(true);
      setInstallIframes(validIframes);
      setEwaSettings(prev => ({
        ...prev,
        iframes: validIframes,
      }));
    };
    runAsyncLoadEwa();

    return () => {
      canceled = true;
    };
  }, [isBuilderInitialised, setGlobalModalContent, t, setEwaSettings, itin?.id, installIframes, readyIframeCount, ewaSettings?.phase, ewaSettings?.isLoading, isIframesInstalled, ewaSettings?.docs]);

  return (
    <>
      <div ref={containerRef} />
    </>
  );
}

export default EWA;